import { useState, type FormEvent } from 'react';
import { api, BladApi, type Kontrahent, type NowaPozycjaCzesci } from '../api';
import { useIdOperacji } from '../operacje';
import { idz } from '../router';
import { EdytorCzesci } from '../components/EdytorCzesci';
import { IkonaWstecz } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import { WyborKontrahenta } from '../components/WyborKontrahenta';
import { Karta, Komunikat, PoleTekstowe, Przycisk, PrzyciskMarki } from '../components/ui';

/**
 * Wystawienie zlecenia montażu — odwzorowanie druku „Zlecenie montażu v1.1"
 * w kolejności, w jakiej się go wypełnia: zamówienie, potem podzespoły, potem części.
 *
 * Wymagane jest tylko to, co na papierze jest wypełnione przy wystawianiu: klient
 * i rodzaj. Podzespoły i numer fabryczny dopisuje monter w trakcie — w chwili
 * wystawiania nikt ich jeszcze nie zna (CLAUDE.md §1 zasady 2 i 4).
 */
export function NewAssemblyView() {
  const operacja = useIdOperacji();

  const [klient, setKlient] = useState<Kontrahent | null>(null);
  const [rodzaj, setRodzaj] = useState<'NOWA' | 'UZYWANA'>('NOWA');
  const [numerZamowienia, setNumerZamowienia] = useState('');
  const [dataRealizacji, setDataRealizacji] = useState('');
  const [producent, setProducent] = useState('');
  const [modelGlowicy, setModelGlowicy] = useState('');
  const [modelNapedu, setModelNapedu] = useState('');
  const [podstawa, setPodstawa] = useState('');
  const [blat, setBlat] = useState('');
  const [inne, setInne] = useState('');
  const [uwagi, setUwagi] = useState('');
  const [czesci, setCzesci] = useState<NowaPozycjaCzesci[]>([]);
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  async function wyslij(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (!klient || zapis) return;

    setZapis(true);
    setBlad(null);
    try {
      const karta = await api.utworzMontaz({
        clientOperationId: operacja.biezacy(),
        customerId: klient.id,
        rodzaj,
        numerZamowienia: numerZamowienia.trim() || undefined,
        dataRealizacji: dataRealizacji || undefined,
        producent: producent.trim() || undefined,
        modelGlowicy: modelGlowicy.trim() || undefined,
        modelNapedu: modelNapedu.trim() || undefined,
        podstawa: podstawa.trim() || undefined,
        blat: blat.trim() || undefined,
        // Każda linia to osobna pozycja — tak samo jak na papierze.
        inne: inne
          .split('\n')
          .map((pozycja) => pozycja.trim())
          .filter(Boolean),
        uwagi: uwagi.trim() || undefined,
        czesci: czesci.filter((pozycja) => pozycja.opis.trim()),
      });
      operacja.zakoncz();
      idz(`/montaze/${karta.montaz.id}`);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się wystawić zlecenia.');
      setZapis(false);
    }
  }

  return (
    <>
      <NaglowekWidoku
        tytul="Nowe zlecenie montażu"
        podtytul="Numer nada system · podzespoły można dopisać później"
        akcje={
          <Przycisk
            type="button"
            onClick={() => idz('/montaze')}
            ikona={<IkonaWstecz rozmiar={16} />}
          >
            Anuluj
          </Przycisk>
        }
      />

      <form onSubmit={wyslij} className="max-w-[760px]" noValidate>
        <Karta
          tytul="Zamówienie"
          wyroznienie
          akcje={<span className="t-small text-n-600">wymagany jest klient i rodzaj</span>}
        >
          <div className="mb-14">
            <WyborKontrahenta wybrany={klient} onWybor={setKlient} />
          </div>

          <div className="grid gap-13 sm:grid-cols-2">
            <label className="block">
              <span className="t-label mb-5 block text-n-700">Rodzaj</span>
              <select
                value={rodzaj}
                onChange={(z) => setRodzaj(z.target.value as 'NOWA' | 'UZYWANA')}
                className="w-full border border-n-300 bg-surface px-13 text-n-900"
              >
                <option value="NOWA">Montaż maszyny nowej</option>
                <option value="UZYWANA">Przygotowanie maszyny używanej</option>
              </select>
            </label>

            <PoleTekstowe
              etykieta="Numer zamówienia"
              podpowiedz="np. 21/PB/04/2026"
              value={numerZamowienia}
              onChange={(z) => setNumerZamowienia(z.target.value)}
              autoComplete="off"
            />

            <PoleTekstowe
              type="date"
              etykieta="Data realizacji"
              value={dataRealizacji}
              onChange={(z) => setDataRealizacji(z.target.value)}
            />
          </div>
        </Karta>

        <Karta
          tytul="Wykaz podzespołów"
          akcje={<span className="t-small text-n-500">przejdą na kartę urządzenia</span>}
        >
          <div className="grid gap-13 sm:grid-cols-2">
            <PoleTekstowe
              etykieta="Producent"
              podpowiedz="np. Juki, Brother, Jack"
              value={producent}
              onChange={(z) => setProducent(z.target.value)}
              autoComplete="off"
            />
            <PoleTekstowe
              etykieta="Model głowicy"
              value={modelGlowicy}
              onChange={(z) => setModelGlowicy(z.target.value)}
              autoComplete="off"
            />
            <PoleTekstowe
              etykieta="Model napędu"
              value={modelNapedu}
              onChange={(z) => setModelNapedu(z.target.value)}
              autoComplete="off"
            />
            <PoleTekstowe
              etykieta="Podstawa"
              value={podstawa}
              onChange={(z) => setPodstawa(z.target.value)}
              autoComplete="off"
            />
            <PoleTekstowe
              etykieta="Blat"
              value={blat}
              onChange={(z) => setBlat(z.target.value)}
              autoComplete="off"
            />
          </div>

          <label className="mt-13 block">
            <span className="t-label mb-5 block text-n-700">
              Inne <span className="t-small text-n-500">— każda pozycja w osobnej linii</span>
            </span>
            <textarea
              value={inne}
              onChange={(z) => setInne(z.target.value)}
              rows={3}
              className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
            />
          </label>
        </Karta>

        <EdytorCzesci czesci={czesci} onZmiana={setCzesci} />

        <Karta tytul="Uwagi">
          <textarea
            value={uwagi}
            onChange={(z) => setUwagi(z.target.value)}
            rows={3}
            placeholder="np. wysyłka na palecie – Rohlig"
            className="w-full border border-n-300 bg-surface px-13 py-9 text-n-900"
          />
        </Karta>

        {blad && (
          <div className="mb-14">
            <Komunikat tresc={blad} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-13">
          <PrzyciskMarki type="submit" disabled={!klient || zapis}>
            {zapis ? 'Wystawianie…' : 'Wystaw zlecenie'}
          </PrzyciskMarki>
          <span className="t-small text-n-500">
            Podpisy złożysz na karcie zlecenia, gdy montaż ruszy.
          </span>
        </div>
      </form>
    </>
  );
}
